/* eslint-disable @typescript-eslint/no-unused-vars */
import type { FormData as ProductFormData, PropertyData } from '../types'
import { serializeProductMedia } from './serializeMedia'

interface ApiData {
  categoriesData?: { data?: Array<{ id: string; name: string }> }
  colorsData?: { data?: Array<{ id: string; name: string; hexValue: string }> }
  materialsData?: { data?: Array<{ id: string; name: string }> }
  sizeGuideData?: unknown
}

const generateUniqueId = () => {
  return Math.random().toString(36).substr(2, 9)
}

export const transformToApiFormat = (
  formData: ProductFormData, 
  propertyData: PropertyData, 
  apiData: ApiData, 
  status: 'DRAFT' | 'PUBLISHED'
): FormData => {
  const { colorsData, materialsData } = apiData
  const multipartFormData = new FormData()
  const imageNames: string[] = []

  const sizes = propertyData.sizes.map((sizeData) => ({
    sizeId: sizeData.sizeId,
    additionalPrice: sizeData.price,
    additionalCostPrice: sizeData.additionalCostPrice,
    materials: sizeData.materials.map((materialData) => ({
      materialId:
        materialsData?.data?.find(
          (m: { name: string }) => m.name === materialData.name
        )?.id || "string",
      additionalPrice: materialData.price,
      additionalCostPrice: materialData.additionalCostPrice,
      colours: materialData.colors.map((colorData) => {
        const colorName = colorsData?.data?.find(
          (c: { hexValue: string }) => c.hexValue === colorData.hex
        )?.name || 'unknown'
        
        // Generate unique names for images and add to FormData
        const colorImageNames = colorData.images.map((image, _index) => {
          const uniqueId = generateUniqueId()
          const imageName = `color-${colorName}-${uniqueId}`
          multipartFormData.append('files', image, imageName)
          return imageName
        })
        imageNames.push(...colorImageNames);

        return {
          colourId:
            colorsData?.data?.find(
              (c: { hexValue: string }) => c.hexValue === colorData.hex
            )?.id || "string",
          additionalPrice: colorData.price,
          additionalCostPrice: colorData.additionalCostPrice,
          quantity: colorData.quantity,
          weight: colorData.weight,
          imageUrls: colorImageNames,
          stockUpTrigger: 5,
        }
      }),
    })),
  }));

  // PE-1283 §7 E4 — product-level media (images + transcoded video). Only emit the
  // key when there is at least one persisted asset so image-only products are byte-
  // for-byte identical to before (backward-compatible).
  const media = serializeProductMedia(formData.media)

  const productData = status === "DRAFT"
    ? {
        name: formData.name,
        description: formData.details,
        categoryId: formData.category,
        sizeGuideId: formData.styleGuide || "string",
        basePrice: parseInt(formData.price) || 0,
        baseCostPrice: parseInt(formData.baseCostPrice) || 0,
        productionTimeInDays: parseInt(formData.productionTimeInDays) || 0,
        discountPercentage: formData.discountPercentage || 0,
        sizes,
        ...(media.length > 0 ? { media } : {}),
      }
    : {
        name: formData.name,
        description: formData.details,
        categoryId: formData.category,
        sizeGuideId: formData.styleGuide || "string",
        basePrice: parseInt(formData.price) || 0,
        baseCostPrice: parseInt(formData.baseCostPrice) || 0,
        productionTimeInDays: parseInt(formData.productionTimeInDays) || 0,
        discountPercentage: formData.discountPercentage || 0,
        status,
        sizes,
        ...(media.length > 0 ? { media } : {}),
      };

  multipartFormData.append('productData', JSON.stringify(productData))
  
  return multipartFormData
}